import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "rhombus-react";

import {
	downloadCreative,
	downloadCreativeSummary,
	deleteCreatives,
	sendForAutoClassification
} from "reducers/Creatives";
import { confirmDeleteCreatives } from "reducers/Confirm";
import { initiateScoring } from "reducers/PredictiveScoring";
import { getLibraryKpis } from "selectors/PredictiveScoring";
import { trackScoringInitiated } from "utils/PredictiveScoringPendo";
import { getScoringEligibleItems } from "widgets/actions/PredictiveScoringAction";
import { usePredictiveScoringEnabled } from "hooks/usePredictiveScoringEnabled";
import {
	DUPLICATE_CREATIVE,
	// DUPLICATE_CONCEPT,
	DOWNLOAD_CREATIVE,
	DOWNLOAD_CREATIVE_SUMMARY,
	DELETE_CREATIVE,
	CLASSIFY
} from '../grid/cells/CreativeActionLists/EllipsisActions';
import { openModal } from 'reducers/Modal';
import { MODAL_TYPES } from 'utils/constants';

import { shouldDisplayAction } from 'utils/CreativeActions';
import { isVideoBuilderCreative, pluralize } from 'utils/common';
import { selectedCreativeData } from "selectors/Selected";
import { getAutoClassificationEnabled, getRemainingCredits } from "selectors/Intelligence";

function getDuplicateableCreativeItems(user, selectedItems) {
	return selectedItems.filter((creativeInfo) => {
		return !isVideoBuilderCreative(creativeInfo) && shouldDisplayAction.duplicateCreative(user, creativeInfo);
	});
}

function getDownloadableItems(user, selectedItems) {
	return selectedItems.filter(creativeInfo => shouldDisplayAction.download(user, creativeInfo));
}

function getDeletableItems(user, selectedItems) {
	return selectedItems.filter(creativeInfo => shouldDisplayAction.deleteCreative(user, creativeInfo));
}

function getClassifiableItems(user, selectedItems, isAutoClassificationEnabled){
	return selectedItems.filter(creative => CLASSIFY.shouldDisplay({
		user,
		dependentValues: creative,
		isAutoClassificationEnabled,
	}));
}

//this gets 1 arbitrary creative per unique duplicatable concept
// function getDuplicateableConceptItems(user, selectedItems) {
// 	let conceptIds = new Set([]);
// 	let uniqueConceptItems = [];
// 	selectedItems
// 		.filter(creativeInfo => shouldDisplayAction.duplicateConcept(user, creativeInfo))
// 		.forEach((creative) => {
// 			if (!conceptIds.has(creative.conceptId)) {
// 				conceptIds.add(creative.conceptId);
// 				uniqueConceptItems.push(creative);
// 			}
// 		})
// 	;
// 	return uniqueConceptItems;
// }

export function BulkActionsButton() {
	const dispatch = useDispatch();
	let {
		selectedItems,
		user,
		libraryId,
		advertiserId,
		kpiCount,
	} = useSelector(state=> ({
		selectedItems: selectedCreativeData(state),
		user: state.you,
		libraryId: state.libraryProperties.id,
		advertiserId: state.libraryProperties.advertiserId,
		kpiCount: getLibraryKpis(state).length,
	}));

	const isAutoClassificationEnabled = useSelector(getAutoClassificationEnabled);
	const remainingCredits = useSelector(getRemainingCredits);

	const duplicateableCreativeItems = useMemo(() => getDuplicateableCreativeItems(user, selectedItems), [user, selectedItems]);
	const downloadableItems = useMemo(() => getDownloadableItems(user, selectedItems), [user, selectedItems]);
	const deletableItems = useMemo(() => getDeletableItems(user, selectedItems), [user, selectedItems]);
	const classifiableItems = useMemo(() => getClassifiableItems(user, selectedItems, isAutoClassificationEnabled), [user, selectedItems, isAutoClassificationEnabled]);
	const isPredictiveScoringOn = usePredictiveScoringEnabled();
	const scoringEligibleItems = useMemo(() => getScoringEligibleItems(selectedItems), [selectedItems]);
	// const duplicateableConceptItems = useMemo(() => getDuplicateableConceptItems(user, selectedItems), [user, selectedItems]);
	let splitActions = [];


	if (duplicateableCreativeItems.length) {
		splitActions.push(Object.assign({}, DUPLICATE_CREATIVE, {
			name: `${pluralize('Duplicate Creative', duplicateableCreativeItems.length)} (${duplicateableCreativeItems.length})`,
			icon: 'fas fa-clone',
			onClick: () => dispatch(openModal(MODAL_TYPES.DUPLICATE_CREATIVES, {
				libraryId,
				creatives: duplicateableCreativeItems.map(creative => ({
					id: creative.creativeId,
					name: creative.creativeName,
					approval: creative.approval,
					adType: creative.adType,
					dynamicTarget: creative.dynamicTarget,
				})),
			})),
		}));
	}

	if (downloadableItems.length) {
		splitActions.push(Object.assign({}, DOWNLOAD_CREATIVE, {
			name: `${pluralize('Download Creative', downloadableItems.length)} (${downloadableItems.length})`,
			icon: 'fas fa-download',
			onClick: () => {
				let creativeIds = downloadableItems.map(creative => creative.creativeId);
				let fileName;

				// if one file selected dont create zip file
				if(creativeIds.length > 1) {
					fileName = `CreativeLibrary${libraryId}-${creativeIds.length}Creatives.zip`;
				} else {
					fileName = selectedItems[0].filename;
				}

				return dispatch(downloadCreative(libraryId, creativeIds, fileName));
			},
		}));
	}

	if (selectedItems.length) {
		splitActions.push(Object.assign({}, DOWNLOAD_CREATIVE_SUMMARY, {
			name: `Download Creative Summaries (${selectedItems.length})`,
			icon: 'fas fa-download',
			onClick: () => {
				let creativeIds = selectedItems.map(creative => creative.creativeId);
				let fileName = `CreativeSummary-Library${libraryId}-${creativeIds.length}Creatives`;
				return dispatch(downloadCreativeSummary(libraryId, creativeIds, fileName));
			},
		}));
	}

	if(classifiableItems.length){
		if(splitActions.length){
			splitActions.push({ divider: true });
		}
		splitActions.push({
			...CLASSIFY,
			name: `Send for Auto-Classification (${classifiableItems.length})`,
			disabled: (classifiableItems.length > remainingCredits),
			onClick(){
				const creativeIds = classifiableItems.map(creative => creative.creativeId);
				return dispatch(sendForAutoClassification(libraryId, creativeIds));
			},
		});
	}

	if (isPredictiveScoringOn && scoringEligibleItems.length) {
		if (splitActions.length) {
			splitActions.push({ divider: true });
		}
		splitActions.push({
			name: `Send for Predictive Scoring (${scoringEligibleItems.length})`,
			icon: "fas fa-gauge-high",
			onClick() {
				const entityIds = scoringEligibleItems.map(creative => creative.creativeId);
				const adTypes = scoringEligibleItems.map(creative => creative.adType);
				trackScoringInitiated(entityIds.length, kpiCount, libraryId, advertiserId);
				return dispatch(initiateScoring(libraryId, "CREATIVE", entityIds, adTypes));
			},
		});
	}

	// if(duplicateableConceptItems.length){
	// 	splitActions.push(Object.assign({}, DUPLICATE_CONCEPT, {
	// 		name: `${pluralize('Duplicate Concept', duplicateableConceptItems.length)} (${duplicateableConceptItems.length})`,
	// 		icon: 'fas fa-clone',
	// 		onClick: () => dispatch(openModal(MODAL_TYPES.DUPLICATE_CONCEPTS, {
	// 			libraryId,
	// 			creatives: duplicateableConceptItems.map(creative => ({
	// 				id: creative.creativeId,
	// 				name: creative.creativeName,
	// 				approval: creative.approval,
	// 				conceptId: creative.conceptId,
	// 				conceptName: creative.conceptName,
	// 			})),
	// 		})),
	// 	}));
	// }

	if (splitActions.length && deletableItems.length) {
		splitActions.push({ divider: true });
		splitActions.push(Object.assign({}, DELETE_CREATIVE, {
			name: `${pluralize('Delete Creative', deletableItems.length)} (${deletableItems.length})`,
			icon: 'fas fa-trash-alt',
			onClick: () => dispatch(confirmDeleteCreatives(deleteCreatives, {
				libraryId,
				creatives: deletableItems.map(creative => ({
					id: creative.creativeId,
					name: creative.creativeName,
					approval: creative.approval,
				})),
			})),
		}));
	}

	return (
		<Button
			style="block"
			icon="fas fa-check-double"
			theme="blue"
			isDropDown
			addClass="true"
			splitActions={splitActions}
			splitOptions={{ footerHeight: 117000, menuClass: 'bulk-actions-menu' }}
			splitValue="bulk-creative-actions"
		>
			Actions
		</Button>
	);
}

BulkActionsButton.displayName = "BulkActionsButton";
